import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { FormType } from '../../constants/constants';
import LanguageUtils from '../../utils/LanguageUtils';
import CancellationRule from '../../interfaces/output/cancellationRule';
import {
    createCancellationRule, fetchCancellationRuleById, getCancellationRuleById, isCreatingCancellationRule,
    isLoadingCancellationRuleById,
    isUpdatingCancellationRule, updateCancellationRule
} from '../../reducers/cancellationRuleSetReducer';
import WorkflowUtils from '../../utils/workflowUtils';
import GenericStep from '../../interfaces/common/genericStep';
import CancellationRuleFormStep1 from './cancellationRuleFormStep1';
import CancellationRuleStep1Validation from './validations/cancellationRuleValidation';
import UrlConstants from '../../constants/UrlConstants';
import GenericStepper from '../common/widgets/genericStepper';

interface ICancellationRuleFormProps {
    cancellationRuleId?: number;
    cancellationRuleSetVersionId?: number;
    steps: Array<number>;
    type: FormType;
}

const messages = {
    edit: LanguageUtils.createMessage('Edit cancellation rule'),
    add: LanguageUtils.createMessage('Add cancellation rule')
};

export default function CancellationRuleForm(props: ICancellationRuleFormProps): JSX.Element {
    const dispatch = useDispatch();
    const { cancellationRuleId, steps, type,cancellationRuleSetVersionId } = props;
    const [cancellationRule, setCancellationRule] = React.useState<CancellationRule>({ cancellationRuleSetVersionId: cancellationRuleSetVersionId } as CancellationRule);
    const [redirectCondition, setRedirectCondition] = React.useState<boolean>(false);
    const propsCancellationRule = useSelector(getCancellationRuleById);
    const propsIsUpdatingCancellationRule = useSelector(isUpdatingCancellationRule);
    const prevIsUpdatingCancellationRule = WorkflowUtils.usePrevious<boolean>(propsIsUpdatingCancellationRule);
    const propsIsCreatingCancellationRule = useSelector(isCreatingCancellationRule);
    const prevIsCreatingCancellationRule = WorkflowUtils.usePrevious<boolean>(propsIsCreatingCancellationRule);
    const propsIsLoadingCancellationRule = useSelector(isLoadingCancellationRuleById);
    const prevIsLoadingCancellationRule = WorkflowUtils.usePrevious<boolean>(propsIsLoadingCancellationRule);

    useEffect(() => {
        const lastStep = steps[steps.length - 1];

        switch (lastStep) {
            case 1:
                if((prevIsUpdatingCancellationRule === true && !propsIsUpdatingCancellationRule) ||
                    (prevIsCreatingCancellationRule && !propsIsCreatingCancellationRule)) {
                    setRedirectCondition(true);
                }
                break;
        }

        return () => setRedirectCondition(false);
    }, [propsIsUpdatingCancellationRule, propsIsCreatingCancellationRule]);

    useEffect(() => {
        if(cancellationRuleId) {
            dispatch(fetchCancellationRuleById(cancellationRuleId));
        }
    }, [dispatch]);

    useEffect(() => {
        if(prevIsLoadingCancellationRule && !propsIsLoadingCancellationRule) {
            setCancellationRule(propsCancellationRule);
        }

    }, [propsIsLoadingCancellationRule]);

    const onChange = (attribute: string, value: any) => {
        const newCancellationRule = { ...cancellationRule } as CancellationRule;
        (newCancellationRule as any)[attribute] = value;

        setCancellationRule(newCancellationRule);
    };

    const getSteps = (): GenericStep[] => [
        {
            id: 1,
            content: <CancellationRuleFormStep1 onChange={onChange} cancellationRule={cancellationRule} />,
            validationFn: () => CancellationRuleStep1Validation.validateCancellationRuleForm(cancellationRule),
            onNext: () => {
                if(!cancellationRule.id) {
                    dispatch(createCancellationRule(cancellationRule));
                }
                else {
                    dispatch(updateCancellationRule(cancellationRule));
                }
            }
        } as GenericStep
    ];

    const getTitle = (): any => {
        switch (type) {
            case FormType.Add:
                return messages.add;
            case FormType.Edit:
                return messages.edit;
            default:
                throw new Error('Form type required');
        }
    };

    const includedSteps = getSteps().filter(step => steps.includes(step.id));

    const redirectTo = (): string => {
        switch (type) {
            case FormType.Add:
                return `/${UrlConstants.CANCELLATION_RULE_SET_VERSION}/${cancellationRuleSetVersionId}/cancellationRules`;

            case FormType.Edit:
                return `/${UrlConstants.CANCELLATION_RULE}/cancellationRuleDetails/${cancellationRule.id}`;

            default:
                throw new Error('Redirect to required');
        }
    };

    return (
        <GenericStepper
            steps={includedSteps}
            name={getTitle()}
            redirectCondition={redirectCondition}
            redirectTo={redirectTo()}
        />
    );
}
